/**
 * Spanish IEDMT (Impuesto Especial sobre Determinados Medios de Transporte)
 *
 * Registration tax for all vehicles registered in Spain for the first time.
 * Based on CO2 emissions (WLTP) with 5 brackets from 0% to 20%.
 * Used cars apply Annex IV depreciation to determine fiscal value.
 *
 * IEDMT = fiscalValue × taxRate
 * where fiscalValue = purchasePrice × retentionPercentage(age)
 *
 * Reference: Agencia Tributaria — Modelo 576
 */

import { normalizeFuelForDB, WLTP_NEDC_RATIOS } from '../utils/car-data.js';

// ============================================
// CO2-BASED TAX BRACKETS (WLTP)
// ============================================

function getIEDMTRate(co2) {
  if (co2 <= 120) return 0;
  if (co2 <= 160) return 0.0475;
  if (co2 <= 200) return 0.0975;
  if (co2 <= 250) return 0.1475;
  return 0.20;
}

// ============================================
// ANNEX IV DEPRECIATION (RETENTION PERCENTAGES)
// ============================================
// These represent the % of market value retained for tax purposes.
// A 5-year-old car retains 39% of its value → fiscal value = price × 0.39

function getRetentionPercentage(age) {
  if (age < 1)  return 1.00;
  if (age < 2)  return 0.84;
  if (age < 3)  return 0.67;
  if (age < 4)  return 0.56;
  if (age < 5)  return 0.47;
  if (age < 6)  return 0.39;
  if (age < 7)  return 0.34;
  if (age < 8)  return 0.28;
  if (age < 9)  return 0.24;
  if (age < 10) return 0.19;
  if (age < 11) return 0.17;
  if (age < 12) return 0.13;
  return 0.10; // 12+ years: minimum 10%
}

// ============================================
// CO2 EXTRACTION (WLTP preferred)
// ============================================

function extractCO2(car) {
  // IEDMT uses WLTP values
  if (car.co2_emissions_wltp) return car.co2_emissions_wltp;

  // Convert NEDC → WLTP if only NEDC available
  if (car.co2_emissions_nedc) {
    const fuelType = normalizeFuelForDB(car.fuel_type) || 'petrol';
    const ratio = WLTP_NEDC_RATIOS[fuelType] || WLTP_NEDC_RATIOS.default;
    return Math.round(car.co2_emissions_nedc * ratio);
  }

  // Estimate based on fuel type and year (conservative)
  const fuelType = normalizeFuelForDB(car.fuel_type) || 'petrol';
  const year = car.year || 2020;

  if (fuelType === 'electric') return 0;
  if (fuelType === 'diesel') return year >= 2020 ? 140 : 160;
  if (fuelType === 'petrol' || fuelType === 'gasoline') return year >= 2020 ? 180 : 200;
  if (fuelType === 'hybrid' || fuelType === 'plug-in hybrid' || fuelType === 'phev') return 90;

  return 180; // Conservative default
}

// ============================================
// MAIN IEDMT CALCULATION
// ============================================

/**
 * Calculate Spanish IEDMT (Registration Tax)
 *
 * @param {Object} car - Car object with properties:
 *   - price: Purchase price in euros
 *   - year: Registration year
 *   - fuel_type: Fuel type (petrol/diesel/electric/hybrid)
 *   - co2_emissions_wltp: CO2 under WLTP (preferred)
 *   - co2_emissions_nedc: CO2 under NEDC (fallback, will be converted)
 *   - make: Car manufacturer (for debug logging)
 *   - model: Car model (for debug logging)
 * @returns {number} IEDMT tax amount in euros
 */
export function calculateSpainIEDMT(car) {
  const currentYear = new Date().getFullYear();
  const year = car.year || currentYear;
  const age = Math.max(0, currentYear - year);
  const price = car.price || 0;

  const fuelType = normalizeFuelForDB(car.fuel_type) || 'petrol';

  // Electric vehicles are exempt (0 g/km CO2 → 0% bracket)
  if (fuelType === 'electric') {
    return 0;
  }

  const co2 = extractCO2(car);
  const taxRate = getIEDMTRate(co2);

  // 0% bracket — no tax regardless of value
  if (taxRate === 0) {
    return 0;
  }

  // Fiscal value = purchase price × retention percentage (Annex IV depreciation)
  const retention = getRetentionPercentage(age);
  const fiscalValue = price * retention;

  const tax = fiscalValue * taxRate;
  const finalTax = Math.max(0, Math.round(tax));

  // Debug logging (same pattern as Portugal ISV)
  if (console && console.log) {
    console.log(`🇪🇸 IEDMT Calculation for ${car.make} ${car.model} ${year}:`, {
      co2,
      fuelType,
      taxRate: `${(taxRate * 100).toFixed(2)}%`,
      age: `${age} years`,
      price: `€${price.toLocaleString()}`,
      retention: `${(retention * 100).toFixed(0)}%`,
      fiscalValue: `€${Math.round(fiscalValue).toLocaleString()}`,
      finalIEDMT: `€${finalTax.toLocaleString()}`
    });
  }

  return finalTax;
}

export default calculateSpainIEDMT;
